import kebabCase from 'lodash/kebabCase';
import React, { MouseEventHandler, PropsWithChildren, useReducer } from 'react';
import colors from '../../helpers/colors';
import componentProps from '../../helpers/componentProps';
import { useIsPrinting } from '../../hooks/usePrinting';
import { applyComponentTheme } from '../../theme';
import Collapsible from '../Collapsible';
import { GridItemProps } from '../Grid';
import InnerAccordionSection from './InnerAccordionSection';
import SecondaryAccordion from './SecondaryAccordion';
import { accordionWidthInColumns as defaultGridItemProps } from './layout';
import {
  AccordionGrid,
  AccordionGridItem,
  InnerSectionsWrapper,
  OuterSection,
  SectionButton,
  SectionTitle,
  StyledIcon,
} from './styles';
import { AccordionEntry, AccordionProps } from './types';

type AccordionSectionProps = {
  index: number;
  title: string;
  initiallyDisplayEntry: boolean;
  forceOpenForPrint?: boolean;
  useSmallIcons?: boolean;
  onExpand?: MouseEventHandler;
  onCollapse?: MouseEventHandler;
};

const AccordionSection: React.FC<PropsWithChildren<AccordionSectionProps>> = ({
  index,
  title,
  initiallyDisplayEntry,
  forceOpenForPrint,
  useSmallIcons,
  onExpand,
  onCollapse,
  children,
}) => {
  const isPrinting = useIsPrinting();

  const [displayEntryState, toggleDisplayEntryState] = useReducer(
    (state) => !state,
    initiallyDisplayEntry
  );

  const displayForPrinting = forceOpenForPrint && isPrinting;
  const displayEntry = !!(displayEntryState || displayForPrinting);

  const sectionId = `accordion-section_${kebabCase(title)}`;
  const sectionEntryId = `accordion-section_entry_${kebabCase(title)}_${index}`;

  const onClickWithCallback: MouseEventHandler = (event) => {
    if (onExpand && !displayEntry) {
      onExpand(event);
    }
    if (onCollapse && displayEntry) {
      onCollapse(event);
    }
    if (!displayForPrinting) {
      toggleDisplayEntryState();
    }
  };

  const animationTimeMs = 600;

  return (
    <OuterSection>
      <SectionButton
        id={sectionId}
        onClick={onClickWithCallback}
        aria-expanded={displayEntry}
        aria-controls={sectionEntryId}
        type="button">
        <SectionTitle>{title}</SectionTitle>
        <StyledIcon
          name={displayEntry ? 'minus' : 'plus'}
          size={useSmallIcons ? 'small' : 'medium'}
          color={colors.core01}
        />
      </SectionButton>
      <Collapsible
        id={sectionEntryId}
        isOpened={displayEntry}
        animationTimeMs={animationTimeMs}
        role="region"
        aria-labelledby={sectionId}>
        <InnerSectionsWrapper isVisible={displayEntry} animationTimeMs={animationTimeMs}>
          {children}
        </InnerSectionsWrapper>
      </Collapsible>
    </OuterSection>
  );
};

const Accordion: React.FC<AccordionProps> = ({
  accordionEntries,
  initiallyDisplayEntries,
  forceOpenForPrint,
  useSmallIcons,
  accordionGridItemProps,
  ...props
}) => {
  const columnsByBreakpoint: GridItemProps = {
    desktop: accordionGridItemProps?.desktop ?? defaultGridItemProps.desktop,
    tabletLandscape:
      accordionGridItemProps?.tabletLandscape ?? defaultGridItemProps.tabletLandscape,
    tabletPortrait:
      accordionGridItemProps?.tabletPortrait ?? defaultGridItemProps.tabletPortrait,
    mobile: accordionGridItemProps?.mobile ?? defaultGridItemProps.mobile,
  };
  return (
    <AccordionGrid {...componentProps(props)}>
      <AccordionGridItem {...columnsByBreakpoint}>
        {accordionEntries.map((entry, index) => (
          <AccordionSection
            title={entry.title}
            initiallyDisplayEntry={entry.initiallyDisplay ?? initiallyDisplayEntries}
            forceOpenForPrint
            useSmallIcons={useSmallIcons}
            index={index}
            onExpand={entry.onExpand}
            onCollapse={entry.onCollapse}
            key={entry.title}>
            {entry.content}
          </AccordionSection>
        ))}
      </AccordionGridItem>
    </AccordionGrid>
  );
};

export { InnerAccordionSection, SecondaryAccordion };
export type { AccordionEntry, AccordionProps };
export default applyComponentTheme(Accordion, 'Accordion');
